import type {
  IntegrationCredential,
  IntegrationDefinition,
  MaskedCredentialField,
} from '@protoss/shared';
import {
  AzureDevOpsIcon,
  Button,
  DatabricksIcon,
  JiraIcon,
  MultiSelect,
  PowerBiIcon,
  SkeletonBlock,
  SlackIcon,
  TagInput,
  TextArea,
} from '@protoss/ui';
import * as React from 'react';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';
import { PageErrorState } from '../components/shared/PageErrorState';
import { VendorCard, VendorCardSkeleton } from '../components/shared/VendorCard';
import {
  ContextDrawer,
  MainContentHeaderActionsContext,
  MainContentSidePanelContext,
} from '../components/shell';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useDirtyState } from '../hooks/useDirtyState';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { apiFetch } from '../lib/apiClient';
import { formatDate } from '../lib/datetime';
import { firebaseAuth } from '../lib/firebase';
import './integrations-page.css';

// ─── Provider logo resolver ──────────────────────────────────────────────────

/**
 * ProviderLogo - resolves a provider logo component from an integration slug.
 *
 * Falls back to the first letter of the integration name when no specific
 * logo component is available. Add new providers here as they are implemented
 * in packages/ui/src/icons/providers/.
 *
 * See context/iconography.md for usage rules.
 */
function hasLogo(slug: string): boolean {
  return (
    slug === 'azure-devops' ||
    slug === 'databricks' ||
    slug === 'google-calendar' ||
    slug === 'google-drive' ||
    slug === 'jira' ||
    slug === 'power-bi' ||
    slug === 'slack' ||
    slug === 'zoom'
  );
}

function ProviderLogo({ slug, name, size }: { slug: string; name: string; size: number }) {
  if (slug === 'azure-devops') {
    return <AzureDevOpsIcon size={size} />;
  }
  if (slug === 'databricks') {
    return <DatabricksIcon size={size} />;
  }
  if (slug === 'power-bi') {
    return <PowerBiIcon size={size} />;
  }
  if (slug === 'jira') {
    return <JiraIcon size={size} />;
  }
  if (slug === 'google-calendar') {
    return (
      <img
        src="/integrations/google-calendar/logo.png"
        alt={`${name} logo`}
        width={size}
        height={size}
        style={{ width: size, height: size, objectFit: 'contain' }}
      />
    );
  }
  if (slug === 'google-drive') {
    return (
      <img
        src="/integrations/google-drive/logo.png"
        alt={`${name} logo`}
        width={size}
        height={size}
        style={{ width: size, height: size, objectFit: 'contain' }}
      />
    );
  }
  if (slug === 'slack') {
    return <SlackIcon size={size} />;
  }
  if (slug === 'zoom') {
    return (
      <img
        src="/integrations/zoom/logo.png"
        alt={`${name} logo`}
        width={size}
        height={size}
        style={{ width: size, height: size, objectFit: 'contain' }}
      />
    );
  }
  return <span className="integration-catalog-logo-placeholder">{name.charAt(0)}</span>;
}

// ─── Credential schema – field descriptor ────────────────────────────────────

interface SchemaField {
  label: string;
  type?: string;
  description?: string;
  example?: string;
  required?: boolean;
}

type CredentialSchema = Record<string, SchemaField>;

function getSchemaFields(schema: Record<string, unknown>): [string, SchemaField][] {
  return Object.entries(schema).map(([key, raw]) => {
    const field = (raw && typeof raw === 'object' ? raw : {}) as SchemaField;
    return [key, { ...field, label: field.label ?? key }];
  });
}

// ─── Status badge component ───────────────────────────────────────────────────

type BadgeVariant =
  | 'connected'
  | 'not-connected'
  | 'active'
  | 'disabled'
  | 'archived'
  | 'unknown'
  | 'healthy'
  | 'degraded'
  | 'error';

function StatusBadge({ variant, label }: { variant: BadgeVariant; label: string }) {
  return <span className={`integration-badge integration-badge-${variant}`}>{label}</span>;
}

// ─── Skeleton components ──────────────────────────────────────────────────────

function IntegrationsConnectedCardSkeleton() {
  return <VendorCardSkeleton badges />;
}

function IntegrationsDetailSkeleton() {
  return (
    <div
      className="integrations-detail-view"
      aria-busy="true"
      aria-label="Loading integration details"
    >
      <div className="integrations-detail-provider">
        <div className="integrations-detail-provider-logo">
          <SkeletonBlock />
        </div>
        <div className="integrations-skeleton-provider-name">
          <SkeletonBlock />
        </div>
      </div>
      <div className="integrations-skeleton-badge">
        <SkeletonBlock />
      </div>
      <div className="integrations-skeleton-action">
        <SkeletonBlock />
      </div>
      <div className="integrations-skeleton-action">
        <SkeletonBlock />
      </div>
    </div>
  );
}

// ─── Catalog panel (right rail) ───────────────────────────────────────────────

// ─── Integration category metadata ───────────────────────────────────────────

const INTEGRATION_CATEGORIES: Record<string, { label: string; description: string }> = {
  communication: {
    label: 'Communication',
    description:
      'Connect communication platforms to exchange messages, ask questions, and get answers without leaving your workflow.',
  },
  calendar: {
    label: 'Calendar Sync',
    description:
      'Sync upcoming meetings, classify participants, and surface insights before each call.',
  },
  devops: {
    label: 'DevOps',
    description: 'Connect version-control and CI/CD systems to enrich your data pipeline.',
  },
  data_platform: {
    label: 'Data Platform',
    description: 'Link data platforms and warehouses to query your data through Protoss.',
  },
};

function categoryMeta(category: string): { label: string; description: string } {
  return (
    INTEGRATION_CATEGORIES[category] ?? {
      label: category.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      description: '',
    }
  );
}

function CatalogPanel({
  catalog,
  isWorkspaceAdmin,
  activeModuleKey,
  onSelect,
  onClose,
}: {
  catalog: IntegrationDefinition[];
  isWorkspaceAdmin: boolean;
  activeModuleKey: string | null;
  onSelect: (def: IntegrationDefinition) => void;
  onClose: () => void;
}) {
  const visible = catalog.filter((def) => {
    const adminVisible = isWorkspaceAdmin || def.slug === 'google-calendar';
    const moduleAllowed =
      !def.allowedModules ||
      def.allowedModules.length === 0 ||
      activeModuleKey == null ||
      def.allowedModules.includes(activeModuleKey);
    return adminVisible && moduleAllowed;
  });

  if (visible.length === 0) {
    return (
      <div className="wp-drawer-empty">
        <p className="wp-drawer-empty-text">No integrations available.</p>
        <button type="button" className="wp-drawer-btn wp-drawer-btn--secondary" onClick={onClose}>
          Close
        </button>
      </div>
    );
  }

  // Group by category preserving API order
  const grouped = visible.reduce<Map<string, IntegrationDefinition[]>>((acc, def) => {
    const cat = def.category ?? 'other';
    if (!acc.has(cat)) acc.set(cat, []);
    acc.get(cat)!.push(def);
    return acc;
  }, new Map());

  return (
    <div className="integrations-catalog-panel">
      {Array.from(grouped.entries()).map(([category, defs]) => {
        const { label, description } = categoryMeta(category);
        return (
          <div key={category} className="integrations-catalog-group">
            <div className="integrations-catalog-group-header">
              <span className="integrations-catalog-group-label">{label}</span>
              {description && (
                <p className="integrations-catalog-group-description">{description}</p>
              )}
            </div>
            {defs.map((def) => (
              <button
                key={def.uuid}
                type="button"
                className="integrations-catalog-panel-item"
                onClick={() => onSelect(def)}
              >
                <div className={`integration-catalog-logo${hasLogo(def.slug) ? ' has-logo' : ''}`}>
                  <ProviderLogo slug={def.slug} name={def.name} size={24} />
                </div>
                <span className="integration-catalog-name">{def.name}</span>
              </button>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function MaskedCredentialPreview({ fields }: { fields: MaskedCredentialField[] }) {
  if (fields.length === 0) return null;
  return (
    <span className="integration-item-credential-preview">
      {fields
        .map((f) => {
          if (typeof f.value === 'boolean') {
            return f.value ? `${f.key}: configured` : `${f.key}: not set`;
          }
          return `${f.key}: ${String(f.value)}`;
        })
        .join('  ·  ')}
    </span>
  );
}

// ─── Generic form field ───────────────────────────────────────────────────────

function FormField({
  id,
  label,
  type = 'text',
  value,
  onChange,
  hint,
  example,
  required,
}: {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  example?: string;
  required?: boolean;
}) {
  const placeholder = type !== 'password' ? (example ?? undefined) : undefined;
  return (
    <div className="integrations-field">
      <label className="integrations-field-label" htmlFor={id}>
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </label>
      <input
        id={id}
        className="integrations-field-input"
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        required={required}
      />
      {hint && <p className="integrations-field-hint">{hint}</p>}
      {example && type === 'password' && (
        <p className="integrations-field-example">
          <span className="integrations-field-example-label">e.g.</span> {example}
        </p>
      )}
    </div>
  );
}

// ─── Slack connect panel (right pane - OAuth install flow) ──────────────────

/**
 * SlackConnectPanel
 *
 * Shown in the right drawer when a workspace admin clicks the Slack catalog card.
 * Instead of a manual credential form (which would be wrong for OAuth-based installs),
 * this panel explains the integration and provides a single "Add to Slack" button.
 *
 * Flow:
 * 1. Admin clicks "Add to Slack"
 * 2. Frontend calls GET /api/integration/slack/connect (Firebase-authenticated)
 * 3. Server returns { url } - the Slack OAuth v2 authorization URL
 * 4. A popup window opens the Slack authorization page
 * 5. After approval, Slack redirects the popup to /api/integration/slack/callback
 * 6. Server completes the install and redirects the popup to /administration/integrations?slack_connected=1&team=<name>
 * 7. The popup detects window.name === 'slack-oauth', broadcasts SLACK_OAUTH_SUCCESS via BroadcastChannel, and closes
 * 8. IntegrationsPage receives the broadcast, shows a success banner, and reloads
 */
function SlackConnectPanel({ onCancel }: { onCancel: () => void }) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function handleAddToSlack() {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiFetch<{ url: string }>('/api/integration/slack/connect');
      const popup = window.open(result.url, 'slack-oauth', 'width=600,height=700,popup=yes');
      if (!popup) {
        // Popup blocked - fall back to full-page redirect
        window.location.href = result.url;
        return;
      }
      setIsLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start Slack authorization');
      setIsLoading(false);
    }
  }

  return (
    <div className="integrations-panel-form">
      <p className="integrations-panel-heading">Connect Slack</p>
      <p className="integrations-field-hint">
        Connect Protoss to your Slack workspace. Once installed, your team can run{' '}
        <code>/protoss &lt;question&gt;</code> in any channel to query Protoss directly from Slack.
      </p>
      <p className="integrations-field-hint">
        Clicking the button below will open a Slack authorization window. After approving, the
        window will close automatically and your connection will appear here.
      </p>
      {error && (
        <p className="integrations-status-message integrations-status-message-error">{error}</p>
      )}
      <div className="integrations-panel-form-actions">
        <button
          type="button"
          className="integrations-btn integrations-btn-primary"
          onClick={() => void handleAddToSlack()}
          disabled={isLoading}
        >
          {isLoading ? 'Redirecting…' : 'Connect to Slack'}
        </button>
        <button
          type="button"
          className="integrations-btn integrations-btn-secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Google Drive connect panel ───────────────────────────────────────────────

function GoogleDriveConnectPanel({ onCancel }: { onCancel: () => void }) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function handleConnect() {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiFetch<{ url: string }>('/api/integration/google-drive/connect');
      const popup = window.open(result.url, 'google-drive-oauth', 'width=600,height=700,popup=yes');
      if (!popup) {
        window.location.href = result.url;
        return;
      }
      setIsLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start Google Drive authorization');
      setIsLoading(false);
    }
  }

  return (
    <div className="integrations-panel-form">
      <p className="integrations-panel-heading">Connect Google Drive</p>
      <p className="integrations-field-hint">
        Connect coSow to Google Drive. Once connected, you can specify an export directory for your
        product estimates and SOWs.
      </p>
      <p className="integrations-field-hint">
        Clicking the button below will open a Google authorization window where you can choose which
        Google account to use. After approving, the window will close automatically and your
        connection will appear here. This replaces any existing Google Drive connection for this
        workspace - everyone in the workspace shares the connected account and its export folders.
      </p>
      {error && (
        <p className="integrations-status-message integrations-status-message-error">{error}</p>
      )}
      <div className="integrations-panel-form-actions">
        <button
          type="button"
          className="integrations-btn integrations-btn-primary"
          onClick={() => void handleConnect()}
          disabled={isLoading}
        >
          {isLoading ? 'Redirecting…' : 'Connect to Google Drive'}
        </button>
        <button
          type="button"
          className="integrations-btn integrations-btn-secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Zoom user connect section ───────────────────────────────────────────────

/**
 * Shown inside the Zoom credential view pane for all workspace members.
 * Lets users connect / disconnect their personal Zoom account (user-level OAuth).
 *
 * Also exported for use in the user Settings > Connections page.
 */
export function ZoomUserConnectSection({ refreshTrigger }: { refreshTrigger: number }) {
  const [status, setStatus] = React.useState<{
    connected: boolean;
    connectedAt: string | null;
  } | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [actionLoading, setActionLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const loadStatus = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ connected: boolean; connectedAt: string | null }>(
        '/api/zoom/user/status'
      );
      setStatus(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load status');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadStatus();
  }, [loadStatus, refreshTrigger]);

  async function handleConnect() {
    setActionLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ url: string }>('/api/zoom/user/connect');
      window.open(data.url, 'zoom-user-oauth', 'width=600,height=700,popup=yes');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start connect');
      setActionLoading(false);
    }
    setActionLoading(false);
  }

  async function handleDisconnect() {
    setActionLoading(true);
    setError(null);
    try {
      await apiFetch('/api/zoom/user/connection', { method: 'DELETE' });
      await loadStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect');
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <div className="integrations-user-connect-section">
      <div className="integrations-field-label">Your Zoom account</div>
      {loading ? (
        <span className="integrations-field-hint">Loading…</span>
      ) : status?.connected ? (
        <div className="integrations-user-connect-row">
          <span className="integrations-field-hint">
            Connected
            {status.connectedAt ? ` · ${new Date(status.connectedAt).toLocaleDateString()}` : ''}
          </span>
          <button
            type="button"
            className="integrations-btn integrations-btn-secondary"
            onClick={() => void handleDisconnect()}
            disabled={actionLoading}
          >
            {actionLoading ? 'Disconnecting…' : 'Disconnect your Zoom account'}
          </button>
        </div>
      ) : (
        <div className="integrations-user-connect-row">
          <span className="integrations-field-hint">Not connected</span>
          <button
            type="button"
            className="integrations-btn integrations-btn-primary"
            onClick={() => void handleConnect()}
            disabled={actionLoading}
          >
            {actionLoading ? 'Opening…' : 'Connect your Zoom account'}
          </button>
        </div>
      )}
      {error && <span className="integrations-field-error">{error}</span>}
    </div>
  );
}

// ─── Zoom connect panel ─────────────────────────────────────────────────────

/**
 * ZoomConnectPanel
 *
 * Shown when a workspace admin clicks the Zoom catalog card.
 * Opens the Zoom OAuth popup. After approval, Zoom redirects to the backend
 * callback which extracts the account_id and creates the integration credential.
 *
 * Flow:
 * 1. Admin clicks "Connect with Zoom"
 * 2. Frontend calls GET /api/zoom/oauth/connect (Firebase-authenticated)
 * 3. Server returns { url } - the Zoom OAuth authorization URL
 * 4. A popup window opens the Zoom authorization page
 * 5. After approval, Zoom redirects the popup to /zoom/oauth/callback
 * 6. Server completes the install and redirects popup to /oauth/result?provider=zoom&status=connected
 * 7. OAuthResultPage broadcasts ZOOM_OAUTH_SUCCESS via BroadcastChannel and closes
 * 8. IntegrationsPage receives the broadcast, shows success banner, reloads
 */
function ZoomConnectPanel({
  onCancel,
  onPopupClosed,
}: {
  onCancel: () => void;
  onPopupClosed?: () => void;
}) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const popupRef = React.useRef<Window | null>(null);

  // Fallback for environments where BroadcastChannel doesn't fire (e.g. cosow-dev).
  // If BC fires first the parent calls setCreatePane(null), unmounting this component and
  // cleaning up this listener before the focus event fires - so there is no double-call.
  React.useEffect(() => {
    const handleFocus = () => {
      if (popupRef.current?.closed) {
        popupRef.current = null;
        onPopupClosed?.();
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [onPopupClosed]);

  async function handleConnect() {
    setIsLoading(true);
    setError(null);
    try {
      const result = await apiFetch<{ url: string }>('/api/zoom/oauth/connect');
      const popup = window.open(result.url, 'zoom-oauth', 'width=600,height=700,popup=yes');
      if (!popup) {
        // Popup blocked - fall back to full-page redirect
        window.location.href = result.url;
        return;
      }
      popupRef.current = popup;
      setIsLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start Zoom authorization');
      setIsLoading(false);
    }
  }

  return (
    <div className="integrations-panel-form">
      <p className="integrations-panel-heading">Connect Zoom</p>
      <p className="integrations-field-hint">
        Connect Protoss to your Zoom account to enable live meeting transcription via RTMS. Clicking
        the button below will open a Zoom authorization window. After approving, the window will
        close automatically and your Zoom account will be configured.
      </p>
      {error && (
        <p className="integrations-status-message integrations-status-message-error">{error}</p>
      )}
      <div className="integrations-panel-form-actions">
        <button
          type="button"
          className="integrations-btn integrations-btn-primary"
          onClick={() => void handleConnect()}
          disabled={isLoading}
        >
          {isLoading ? 'Redirecting…' : 'Connect with Zoom'}
        </button>
        <button
          type="button"
          className="integrations-btn integrations-btn-secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Create panel (right pane inline form) ──────────────────────────────────

// ─── Google Calendar connect panel ──────────────────────────────────────────

/**
 * GoogleCalendarConnectPanel
 *
 * Shown when any workspace member clicks the Google Calendar catalog card.
 * Each user connects their own calendar - this is NOT admin-only.
 *
 * Flow:
 * 1. User clicks "Connect with Google"
 * 2. Frontend calls GET /api/integration/google-calendar/connect (Firebase-authenticated)
 * 3. Server returns { url } - the Google OAuth consent URL (includes userId in state)
 * 4. A popup window opens the Google authorization page
 * 5. After approval, Google redirects the popup to /api/integration/google-calendar/callback
 * 6. Server completes the install and redirects popup to /oauth/result?provider=google-calendar&status=connected
 * 7. OAuthResultPage broadcasts GOOGLE_CALENDAR_OAUTH_SUCCESS via BroadcastChannel and closes
 * 8. IntegrationsPage receives the broadcast, shows success banner, reloads
 */
function GoogleCalendarConnectPanel({
  onCancel,
  onSaved,
  isWorkspaceAdmin,
}: {
  onCancel: () => void;
  onSaved?: () => void;
  isWorkspaceAdmin: boolean;
}) {
  const [isSaving, setIsSaving] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const defaultDomain = firebaseAuth.currentUser?.email?.split('@')[1] ?? '';
  const [internalDomains, setInternalDomains] = React.useState<string[]>(
    defaultDomain ? [defaultDomain] : []
  );
  const [clientId, setClientId] = React.useState('');
  const [clientSecret, setClientSecret] = React.useState('');
  const [hasExistingSecret, setHasExistingSecret] = React.useState(false);

  React.useEffect(() => {
    if (!isWorkspaceAdmin) {
      setIsLoading(false);
      return;
    }
    async function loadConfig() {
      try {
        const data = (await apiFetch('/api/integration/google-calendar/workspace-config')) as {
          configured: boolean;
          internalDomain?: string | null;
          clientId?: string | null;
          hasClientSecret?: boolean;
        };
        if (data.configured) {
          if (data.internalDomain)
            setInternalDomains(data.internalDomain.split(',').filter(Boolean));
          if (data.clientId) setClientId(data.clientId);
          if (data.hasClientSecret) setHasExistingSecret(true);
        }
      } catch {
        // Non-fatal - form starts with defaults
      } finally {
        setIsLoading(false);
      }
    }
    void loadConfig();
  }, [isWorkspaceAdmin]);

  async function handleSave() {
    setIsSaving(true);
    setSaved(false);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        internalDomain: internalDomains.join(','),
      };
      if (clientId) body.clientId = clientId;
      // Only send clientSecret if the user typed something new
      if (clientSecret) body.clientSecret = clientSecret;

      await apiFetch('/api/integration/google-calendar/workspace-config', {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      setSaved(true);
      onSaved?.();
      if (clientSecret) {
        setHasExistingSecret(true);
        setClientSecret('');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="integrations-panel-form">
      <p className="integrations-panel-heading">Google Calendar</p>
      {isWorkspaceAdmin ? (
        <>
          <p className="integrations-field-hint">
            Configure workspace-level settings for Google Calendar. Each team member connects their
            own calendar from the <strong>Meetings</strong> page.
          </p>

          {isLoading ? (
            <div
              className="integrations-gcal-config-skeleton"
              aria-busy="true"
              aria-label="Loading configuration"
            >
              <div className="integrations-skeleton-field">
                <SkeletonBlock />
              </div>
              <div className="integrations-skeleton-field">
                <SkeletonBlock />
              </div>
              <div className="integrations-skeleton-field">
                <SkeletonBlock />
              </div>
            </div>
          ) : (
            <>
              <div className="integrations-field-group">
                <label className="integrations-field-label" htmlFor="gcal-client-id">
                  OAuth Client ID
                </label>
                <input
                  id="gcal-client-id"
                  type="text"
                  className="integrations-field-input"
                  placeholder="your-client-id.apps.googleusercontent.com"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  disabled={isSaving}
                  autoComplete="off"
                  spellCheck={false}
                />
                <p className="integrations-field-hint">
                  From GCP Console → APIs &amp; Services → Credentials → OAuth 2.0 Client IDs.
                </p>
              </div>

              <div className="integrations-field-group">
                <label className="integrations-field-label" htmlFor="gcal-client-secret">
                  OAuth Client Secret
                  {hasExistingSecret && !clientSecret ? ' (saved - leave blank to keep)' : ''}
                </label>
                <input
                  id="gcal-client-secret"
                  type="password"
                  className="integrations-field-input"
                  placeholder={hasExistingSecret ? '••••••••' : 'Client secret'}
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  disabled={isSaving}
                  autoComplete="new-password"
                />
              </div>

              <div className="integrations-field-group">
                <label className="integrations-field-label" htmlFor="gcal-internal-domain">
                  Internal email domain(s)
                </label>
                <TagInput
                  id="gcal-internal-domain"
                  tags={internalDomains}
                  onChange={setInternalDomains}
                  placeholder="company.com"
                  disabled={isSaving}
                />
                <p className="integrations-field-hint">
                  Used to classify internal vs external calendar attendees.
                </p>
              </div>
            </>
          )}

          {error && (
            <p className="integrations-status-message integrations-status-message-error">{error}</p>
          )}
          {saved && (
            <p className="integrations-status-message integrations-status-message-success">
              Configuration saved.
            </p>
          )}
          <div className="integrations-panel-form-actions">
            <button
              type="button"
              className="integrations-btn integrations-btn-primary"
              onClick={() => void handleSave()}
              disabled={isSaving || isLoading}
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="integrations-btn integrations-btn-secondary"
              onClick={onCancel}
              disabled={isSaving}
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="integrations-field-hint">
            To connect your personal Google Calendar, go to the <strong>Meetings</strong> page and
            click <strong>Connect Google Calendar</strong>.
          </p>
          <div className="integrations-panel-form-actions">
            <button
              type="button"
              className="integrations-btn integrations-btn-secondary"
              onClick={onCancel}
            >
              Close
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function CreatePanel({
  definition,
  onCancel,
  onCreated,
}: {
  definition: IntegrationDefinition;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const [displayName, setDisplayName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [credentials, setCredentials] = React.useState<Record<string, string>>({});
  const [config, setConfig] = React.useState<Record<string, string>>({});
  const [credentialExpiresAt, setCredentialExpiresAt] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const credFields = getSchemaFields(definition.credentialSchema);
  const configFields = getSchemaFields(definition.configSchema);

  const { isDirty, setBaseline, updateCurrent } = useDirtyState<{
    displayName: string;
    description: string;
    credentials: Record<string, string>;
    config: Record<string, string>;
    credentialExpiresAt: string;
  }>({ trimStrings: true });

  React.useEffect(() => {
    setBaseline({
      displayName: '',
      description: '',
      credentials: {},
      config: {},
      credentialExpiresAt: '',
    });
  }, [setBaseline]);

  React.useEffect(() => {
    updateCurrent({
      displayName,
      description,
      credentials,
      config,
      credentialExpiresAt,
    });
  }, [config, credentialExpiresAt, credentials, description, displayName, updateCurrent]);

  useUnsavedChanges(!isSubmitting && isDirty);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      await apiFetch('/api/integration', {
        method: 'POST',
        body: JSON.stringify({
          definitionUuid: definition.uuid,
          displayName,
          description: description || undefined,
          credentials,
          config,
          credentialExpiresAt: credentialExpiresAt
            ? new Date(credentialExpiresAt + 'T00:00:00Z').toISOString()
            : null,
        }),
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unexpected error');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form className="integrations-panel-form" onSubmit={handleSubmit}>
      <FormField
        id="create-display-name"
        label="Display name"
        value={displayName}
        onChange={setDisplayName}
        hint="A friendly label for this connection."
        required
      />
      <FormField
        id="create-description"
        label="Description"
        value={description}
        onChange={setDescription}
        hint="Optional notes about what this connection is used for."
      />

      {configFields.length > 0 && (
        <>
          <p className="integrations-field-section-label">Configuration</p>
          {configFields.map(([key, field]) => (
            <FormField
              key={key}
              id={`create-config-${key}`}
              label={field.label}
              type={field.type ?? 'text'}
              value={config[key] ?? ''}
              onChange={(v) => setConfig((prev) => ({ ...prev, [key]: v }))}
              hint={field.description}
              example={field.example}
              required={field.required}
            />
          ))}
        </>
      )}

      {credFields.length > 0 && (
        <>
          <p className="integrations-field-section-label">Credentials</p>
          {credFields.map(([key, field]) => (
            <FormField
              key={key}
              id={`create-cred-${key}`}
              label={field.label}
              type="password"
              value={credentials[key] ?? ''}
              onChange={(v) => setCredentials((prev) => ({ ...prev, [key]: v }))}
              hint={field.description}
              example={field.example}
              required={field.required}
            />
          ))}
          <FormField
            id="create-expires-at"
            label="Expiry date"
            type="date"
            value={credentialExpiresAt}
            onChange={setCredentialExpiresAt}
            hint="Optional - leave blank if the credential does not expire."
          />
        </>
      )}

      {error && (
        <p className="integrations-status-message integrations-status-message-error">{error}</p>
      )}
      <div className="integrations-panel-form-actions">
        <button
          type="submit"
          className="integrations-btn integrations-btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Connecting…' : 'Connect'}
        </button>
        <button
          type="button"
          className="integrations-btn integrations-btn-secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── Edit panel (right pane inline form) ─────────────────────────────────────

function EditPanel({
  credential,
  onCancel,
  onUpdated,
}: {
  credential: IntegrationCredential;
  onCancel: () => void;
  onUpdated: () => void;
}) {
  const [displayName, setDisplayName] = React.useState(credential.displayName);
  const [description, setDescription] = React.useState(credential.description ?? '');
  const [config, setConfig] = React.useState<Record<string, string>>(() =>
    Object.fromEntries(credential.config.map((c) => [c.key, c.value]))
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const configFields = getSchemaFields(credential.definition.configSchema);

  const { isDirty, setBaseline, updateCurrent } = useDirtyState<{
    displayName: string;
    description: string;
    config: Record<string, string>;
  }>({ trimStrings: true });

  const baselinePayload = React.useMemo(
    () => ({
      displayName: credential.displayName,
      description: credential.description ?? '',
      config: Object.fromEntries(credential.config.map((c) => [c.key, c.value])) as Record<
        string,
        string
      >,
    }),
    [credential.config, credential.description, credential.displayName, credential.uuid]
  );

  React.useEffect(() => {
    setBaseline(baselinePayload);
  }, [baselinePayload, setBaseline]);

  React.useEffect(() => {
    updateCurrent({ displayName, description, config });
  }, [config, description, displayName, updateCurrent]);

  useUnsavedChanges(!isSubmitting && isDirty);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      await apiFetch(`/api/integration/${credential.uuid}`, {
        method: 'PUT',
        body: JSON.stringify({ displayName, description: description || null, config }),
      });
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unexpected error');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form className="integrations-panel-form" onSubmit={handleSubmit}>
      <p className="integrations-panel-heading">Edit settings</p>
      <FormField
        id="edit-display-name"
        label="Display name"
        value={displayName}
        onChange={setDisplayName}
        required
      />
      <FormField
        id="edit-description"
        label="Description"
        value={description}
        onChange={setDescription}
        hint="Optional notes about what this connection is used for."
      />
      {configFields.length > 0 && credential.definition.slug !== 'google-drive' && (
        <>
          <p className="integrations-field-section-label">Configuration</p>
          {configFields.map(([key, field]) => {
            const isReadOnly = credential.definition.slug === 'zoom' && key === 'account_id';
            return isReadOnly ? (
              <div key={key} className="integrations-field">
                <div className="integrations-field-label">{field.label}</div>
                <div className="integrations-field-readonly">{config[key] ?? ''}</div>
                {field.description && (
                  <p className="integrations-field-hint">{field.description}</p>
                )}
              </div>
            ) : (
              <FormField
                key={key}
                id={`edit-config-${key}`}
                label={field.label}
                type={field.type ?? 'text'}
                value={config[key] ?? ''}
                onChange={(v) => setConfig((prev) => ({ ...prev, [key]: v }))}
                hint={field.description}
                example={field.example}
                required={field.required}
              />
            );
          })}
        </>
      )}
      {error && (
        <p className="integrations-status-message integrations-status-message-error">{error}</p>
      )}
      <div className="integrations-panel-form-actions">
        <button
          type="submit"
          className="integrations-btn integrations-btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving…' : 'Save changes'}
        </button>
        <button
          type="button"
          className="integrations-btn integrations-btn-secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── Rotate panel (right pane inline form) ────────────────────────────────────

function RotatePanel({
  credential,
  onCancel,
  onRotated,
}: {
  credential: IntegrationCredential;
  onCancel: () => void;
  onRotated: () => void;
}) {
  const [newCredentials, setNewCredentials] = React.useState<Record<string, string>>({});
  const [credentialExpiresAt, setCredentialExpiresAt] = React.useState(() => {
    if (!credential.credentialExpiresAt) return '';
    // Convert ISO string to YYYY-MM-DD for the date input
    return credential.credentialExpiresAt.slice(0, 10);
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const { isDirty, setBaseline, updateCurrent } = useDirtyState<{
    newCredentials: Record<string, string>;
    credentialExpiresAt: string;
  }>({ trimStrings: true });

  const baselinePayload = React.useMemo(
    () => ({
      newCredentials: {},
      credentialExpiresAt: credential.credentialExpiresAt
        ? credential.credentialExpiresAt.slice(0, 10)
        : '',
    }),
    [credential.credentialExpiresAt, credential.uuid]
  );

  React.useEffect(() => {
    setBaseline(baselinePayload);
  }, [baselinePayload, setBaseline]);

  React.useEffect(() => {
    updateCurrent({ newCredentials, credentialExpiresAt });
  }, [credentialExpiresAt, newCredentials, updateCurrent]);

  useUnsavedChanges(!isSubmitting && isDirty);

  const credFields = getSchemaFields(
    credential.definition.credentialSchema as CredentialSchema as Record<string, unknown>
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      await apiFetch(`/api/integration/${credential.uuid}/rotate`, {
        method: 'PUT',
        body: JSON.stringify({
          credentials: newCredentials,
          credentialExpiresAt: credentialExpiresAt
            ? new Date(credentialExpiresAt + 'T00:00:00Z').toISOString()
            : null,
        }),
      });
      onRotated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unexpected error');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form className="integrations-panel-form" onSubmit={handleSubmit}>
      <p className="integrations-panel-heading">Rotate credentials</p>
      <p className="integrations-field-hint">
        Enter new credentials to replace the existing ones. The previous credentials will be
        permanently overwritten.
      </p>
      {credFields.map(([key, field]) => (
        <FormField
          key={key}
          id={`rotate-cred-${key}`}
          label={field.label}
          type="password"
          value={newCredentials[key] ?? ''}
          onChange={(v) => setNewCredentials((prev) => ({ ...prev, [key]: v }))}
          hint={field.description}
          example={field.example}
          required
        />
      ))}
      <FormField
        id="rotate-expires-at"
        label="Expiry date"
        type="date"
        value={credentialExpiresAt}
        onChange={setCredentialExpiresAt}
        hint="Optional - leave blank if the credential does not expire."
      />
      {error && (
        <p className="integrations-status-message integrations-status-message-error">{error}</p>
      )}
      <div className="integrations-panel-form-actions">
        <button
          type="submit"
          className="integrations-btn integrations-btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Rotating…' : 'Rotate credentials'}
        </button>
        <button
          type="button"
          className="integrations-btn integrations-btn-secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── Delete panel (right pane inline confirmation) ────────────────────────────

function DeletePanel({
  credential,
  onCancel,
  onDeleted,
}: {
  credential: IntegrationCredential;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const isSlack = credential.definition.slug === 'slack';

  async function handleDelete() {
    setIsSubmitting(true);
    setError(null);
    try {
      await apiFetch(`/api/integration/${credential.uuid}`, {
        method: 'DELETE',
        body: JSON.stringify({}),
      });
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unexpected error');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <ConfirmDeleteDialog
      open
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
      title="Remove integration"
      confirmKeyword={credential.displayName}
      confirmLabel="Remove"
      pendingLabel="Removing…"
      isPending={isSubmitting}
      description={
        <>
          {isSlack ? (
            <>
              Are you sure you want to disconnect <strong>{credential.displayName}</strong> from
              this workspace? Protoss will no longer respond to <code>/protoss</code> commands in
              that Slack workspace, and the stored bot token will be permanently deleted.
              <br />
              <br />
              <strong>Note:</strong> The Slack app itself will remain installed in your Slack
              workspace. To fully remove it, also uninstall it from your Slack workspace settings (
              <em>Settings &amp; administration → Manage apps</em>).
            </>
          ) : (
            <>
              Are you sure you want to remove <strong>{credential.displayName}</strong>? The
              encrypted credentials will be permanently deleted and cannot be recovered.
            </>
          )}
          {error && (
            <p className="integrations-status-message integrations-status-message-error">{error}</p>
          )}
        </>
      }
      onConfirm={handleDelete}
    />
  );
}

// ─── Dev-only: Slack test DM sender ──────────────────────────────────────────

function SlackTestDmPanel() {
  const [selectedUserIds, setSelectedUserIds] = React.useState<string[]>([]);
  const [message, setMessage] = React.useState('Hello from Protoss! This is a test message.');
  const [isSending, setIsSending] = React.useState(false);
  const [isSyncing, setIsSyncing] = React.useState(false);
  const [results, setResults] = React.useState<Array<{
    slackUserId: string;
    success: boolean;
    channelId?: string;
    error?: string;
  }> | null>(null);
  const [topError, setTopError] = React.useState<string | null>(null);
  const [slackUsers, setSlackUsers] = React.useState<
    Array<{ slackUserId: string; displayName: string; email: string | null }>
  >([]);
  const [usersLoading, setUsersLoading] = React.useState(true);
  const [usersError, setUsersError] = React.useState<string | null>(null);

  function loadUsers() {
    setUsersLoading(true);
    setUsersError(null);
    apiFetch<{ users: Array<{ slackUserId: string; displayName: string; email: string | null }> }>(
      '/api/integration/slack/users'
    )
      .then((data) => {
        setSlackUsers(data.users);
      })
      .catch((err: unknown) => {
        setUsersError(err instanceof Error ? err.message : 'Failed to load Slack users');
      })
      .finally(() => {
        setUsersLoading(false);
      });
  }

  React.useEffect(() => {
    loadUsers();
  }, []);

  async function handleSyncMembers() {
    setIsSyncing(true);
    setTopError(null);
    try {
      await apiFetch<{ ok: boolean }>('/api/integration/slack/refresh-members', {
        method: 'POST',
      });
      loadUsers();
    } catch (err) {
      setTopError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setIsSyncing(false);
    }
  }

  const userOptions = React.useMemo(
    () =>
      slackUsers.map((u) => ({
        value: u.slackUserId,
        label: u.displayName,
        sublabel: u.email ?? u.slackUserId,
      })),
    [slackUsers]
  );

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (selectedUserIds.length === 0) return;
    setIsSending(true);
    setResults(null);
    setTopError(null);
    try {
      const data = await apiFetch<{
        sent: number;
        results: Array<{
          slackUserId: string;
          success: boolean;
          channelId?: string;
          error?: string;
        }>;
      }>('/api/integration/slack/test-dm', {
        method: 'POST',
        body: JSON.stringify({ slackUserIds: selectedUserIds, message }),
      });
      setResults(data.results);
    } catch (err) {
      setTopError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setIsSending(false);
    }
  }

  const resultLabelByUserId = React.useMemo(() => {
    const map = new Map(slackUsers.map((u) => [u.slackUserId, u.displayName]));
    return map;
  }, [slackUsers]);

  return (
    <div>
      <div className="integrations-detail-divider" />
      <p className="integrations-panel-heading">Dev: Test direct message</p>
      <p className="integrations-field-hint">
        Send a test DM to one or more Slack workspace members via the bot. Requires the{' '}
        <code>im:write</code> OAuth scope to open new DM channels.
      </p>
      {usersError && (
        <p className="integrations-status-message integrations-status-message-error">
          {usersError}
        </p>
      )}
      <form className="integrations-panel-form" onSubmit={(e) => void handleSend(e)}>
        <div className="integrations-field">
          <MultiSelect
            options={userOptions}
            selectedValues={selectedUserIds}
            onSelectionChange={setSelectedUserIds}
            label="Slack users"
            placeholder="Search by name or email…"
            loading={usersLoading}
            required
          />
        </div>
        <div className="integrations-field">
          <TextArea
            id="test-dm-message"
            label="Message"
            value={message}
            rows={4}
            onChange={(e) => setMessage(e.target.value)}
            required
          />
        </div>
        {topError && (
          <p className="integrations-status-message integrations-status-message-error">
            {topError}
          </p>
        )}
        {results && (
          <div>
            {results.map((r) => (
              <p
                key={r.slackUserId}
                className={`integrations-status-message ${
                  r.success
                    ? 'integrations-status-message-success'
                    : 'integrations-status-message-error'
                }`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <span>
                  {resultLabelByUserId.get(r.slackUserId) ?? r.slackUserId}:{' '}
                  {r.success ? `sent → channel ${r.channelId ?? '?'}` : (r.error ?? 'failed')}
                </span>
                <button
                  type="button"
                  aria-label="Dismiss"
                  onClick={() =>
                    setResults((prev) =>
                      prev ? prev.filter((x) => x.slackUserId !== r.slackUserId) : null
                    )
                  }
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    lineHeight: 1,
                    padding: 0,
                    color: 'inherit',
                    opacity: 0.7,
                  }}
                >
                  ×
                </button>
              </p>
            ))}
          </div>
        )}
        <div className="integrations-panel-form-actions">
          <button
            type="submit"
            className="integrations-btn integrations-btn-primary"
            disabled={isSending || selectedUserIds.length === 0}
          >
            {isSending ? 'Sending…' : 'Send DM'}
          </button>
          <button
            type="button"
            className="integrations-btn integrations-btn-secondary"
            disabled={isSyncing || usersLoading}
            onClick={() => void handleSyncMembers()}
          >
            {isSyncing ? 'Syncing…' : 'Refresh members'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Main page component ──────────────────────────────────────────────────────

type RightPaneMode = 'view' | 'edit' | 'rotate' | 'delete';

interface IntegrationsPageProps {
  isWorkspaceAdmin: boolean;
}

/**
 * IntegrationsPage - content component rendered inside AdministrationPage.
 *
 * Displays:
 *  1. Connected section - active credential instances in a grid; clicking a card
 *     opens an inline right pane for viewing and configuring the credential.
 *  2. Catalog section - available integration types (icon + name only); clicking
 *     a catalog card opens the right pane in create/connect mode.
 *
 * Admin-only actions: connect (create panel), edit, rotate, toggle, delete, test
 * (all actions appear in the right pane - no modal dialogs anywhere).
 */
export function IntegrationsPage({ isWorkspaceAdmin }: IntegrationsPageProps) {
  const { activeModuleKey } = useWorkspace();
  const [catalog, setCatalog] = React.useState<IntegrationDefinition[]>([]);
  const [integrations, setIntegrations] = React.useState<IntegrationCredential[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [fetchError, setFetchError] = React.useState<string | null>(null);

  // Slack OAuth post-install banner state
  const [slackBanner, setSlackBanner] = React.useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [gcalBanner, setGcalBanner] = React.useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  // Right pane - selectedCredential is derived from integrations so it stays fresh after reload
  const [selectedCredentialUuid, setSelectedCredentialUuid] = React.useState<string | null>(null);
  const [rpMode, setRpMode] = React.useState<RightPaneMode>('view');
  const selectedCredential = integrations.find((c) => c.uuid === selectedCredentialUuid) ?? null;

  // Create pane - opened when clicking an available catalog card
  const [createPane, setCreatePane] = React.useState<IntegrationDefinition | null>(null);

  // Catalog pane - opened from the header "Connect" button
  const [catalogOpen, setCatalogOpen] = React.useState(false);

  const headerActionsCtx = React.useContext(MainContentHeaderActionsContext);
  const sidePanelCtx = React.useContext(MainContentSidePanelContext);

  const [testingUuid, setTestingUuid] = React.useState<string | null>(null);
  const [testResult, setTestResult] = React.useState<
    Record<string, { ok: boolean; message?: string }>
  >({});
  const [togglingUuid, setTogglingUuid] = React.useState<string | null>(null);
  const [slackReconnecting, setSlackReconnecting] = React.useState(false);
  const [slackRefreshing, setSlackRefreshing] = React.useState(false);

  const loadData = React.useCallback(async () => {
    setFetchError(null);
    try {
      const [catalogResult, listResult] = await Promise.all([
        apiFetch<{ catalog: IntegrationDefinition[] }>('/api/integration/catalog'),
        apiFetch<{ integrations: IntegrationCredential[] }>('/api/integration'),
      ]);
      setCatalog(catalogResult.catalog);
      setIntegrations(listResult.integrations);
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Failed to load integrations');
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadData();
  }, [loadData]);

  // Project "Connect" button into the MainContentShell header
  React.useEffect(() => {
    if (!headerActionsCtx) return;
    if (isLoading) {
      headerActionsCtx.setHeaderActions(
        <div className="integrations-skeleton-header-btn">
          <SkeletonBlock />
        </div>
      );
    } else {
      headerActionsCtx.setHeaderActions(
        <Button
          size="sm"
          variant="primary"
          onClick={() => {
            setCatalogOpen(true);
            setSelectedCredentialUuid(null);
            setCreatePane(null);
          }}
        >
          Connect
        </Button>
      );
    }
    return () => {
      headerActionsCtx.setHeaderActions(null);
    };
  }, [headerActionsCtx, isLoading]);
  // window.postMessage (fallback for browsers without BroadcastChannel support).
  React.useEffect(() => {
    function handleOAuthResult(data: {
      type: string;
      team?: string | null;
      error?: string | null;
    }) {
      if (data.type === 'SLACK_OAUTH_SUCCESS') {
        const teamName = data.team ?? null;
        setCreatePane(null);
        setSlackBanner({
          type: 'success',
          message: `Slack connected successfully${teamName ? ` - ${decodeURIComponent(teamName)}` : ''}.`,
        });
        void loadData();
      } else if (data.type === 'SLACK_OAUTH_ERROR') {
        const errorCode = decodeURIComponent((data.error as string | null) ?? 'unknown');
        setSlackBanner({
          type: 'error',
          message: `Slack installation failed: ${errorCode.replace(/_/g, ' ')}.`,
        });
      } else if (data.type === 'GOOGLE_CALENDAR_OAUTH_SUCCESS') {
        setCreatePane(null);
        setGcalBanner({ type: 'success', message: 'Google Calendar connected successfully.' });
        void loadData();
      } else if (data.type === 'GOOGLE_CALENDAR_OAUTH_ERROR') {
        const errorCode = decodeURIComponent((data.error as string | null) ?? 'unknown');
        setGcalBanner({
          type: 'error',
          message: `Google Calendar connection failed: ${errorCode.replace(/_/g, ' ')}.`,
        });
      } else if (data.type === 'ZOOM_OAUTH_SUCCESS') {
        setCreatePane(null);
        void loadData();
      } else if (data.type === 'GOOGLE_DRIVE_OAUTH_SUCCESS') {
        setCreatePane(null);
        void loadData();
      } else if (data.type === 'GOOGLE_DRIVE_OAUTH_ERROR') {
        // error banner for google drive not currently implemented
      }
    }

    // BroadcastChannel - works even when window.opener is cleared by COOP
    let bcSlack: BroadcastChannel | null = null;
    let bcGcal: BroadcastChannel | null = null;
    let bcZoom: BroadcastChannel | null = null;
    let bcGoogleDrive: BroadcastChannel | null = null;
    try {
      bcSlack = new BroadcastChannel('slack-oauth');
      bcSlack.onmessage = (event: MessageEvent) =>
        handleOAuthResult(
          event.data as { type: string; team?: string | null; error?: string | null }
        );
      bcGcal = new BroadcastChannel('google-calendar-oauth');
      bcGcal.onmessage = (event: MessageEvent) =>
        handleOAuthResult(
          event.data as { type: string; team?: string | null; error?: string | null }
        );
      bcZoom = new BroadcastChannel('zoom-oauth');
      bcZoom.onmessage = (event: MessageEvent) =>
        handleOAuthResult(
          event.data as { type: string; team?: string | null; error?: string | null }
        );
      bcGoogleDrive = new BroadcastChannel('google-drive-oauth');
      bcGoogleDrive.onmessage = (event: MessageEvent) =>
        handleOAuthResult(
          event.data as { type: string; team?: string | null; error?: string | null }
        );
    } catch {
      // not supported - rely solely on postMessage fallback
    }

    // postMessage fallback (for same-origin popups where COOP doesn't interfere)
    function handleWindowMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      handleOAuthResult(
        event.data as { type: string; team?: string | null; error?: string | null }
      );
    }
    window.addEventListener('message', handleWindowMessage);

    return () => {
      bcSlack?.close();
      bcGcal?.close();
      bcZoom?.close();
      bcGoogleDrive?.close();
      window.removeEventListener('message', handleWindowMessage);
    };
  }, [loadData]);

  function handleSelectCredential(uuid: string) {
    setSelectedCredentialUuid(uuid);
    setRpMode('view'); // always start in view mode when switching credentials
    setCreatePane(null);
  }

  function handleCloseCreate() {
    setCreatePane(null);
    setCatalogOpen(false);
  }

  function handleCloseDrawer() {
    if (catalogOpen && !createPane && !selectedCredential) {
      setCatalogOpen(false);
    } else if (createPane) {
      setCreatePane(null);
      setCatalogOpen(false);
    } else {
      setSelectedCredentialUuid(null);
      setRpMode('view');
    }
  }

  function handleCredentialUpdated() {
    setRpMode('view');
    void loadData(); // background refresh - selectedCredential auto-updates via derived state
  }

  function handleCredentialDeleted() {
    if (selectedCredential?.definition.slug === 'google-calendar') {
      window.dispatchEvent(new CustomEvent('gcal-workspace-config-changed'));
    }
    setSelectedCredentialUuid(null); // close pane immediately
    setRpMode('view');
    void loadData();
  }

  function handleCreateSuccess() {
    setCreatePane(null);
    setCatalogOpen(false);
    void loadData();
  }

  function handleSelectFromCatalog(def: IntegrationDefinition) {
    setCreatePane(def);
    setCatalogOpen(false);
    setSelectedCredentialUuid(null);
  }

  async function handleTest(credential: IntegrationCredential) {
    setTestingUuid(credential.uuid);
    setTestResult((prev) => ({ ...prev, [credential.uuid]: { ok: false, message: 'Testing…' } }));
    try {
      const result = await apiFetch<{ success: boolean; message?: string }>(
        `/api/integration/${credential.uuid}/test`,
        { method: 'POST', body: JSON.stringify({}) }
      );
      setTestResult((prev) => ({
        ...prev,
        [credential.uuid]: { ok: result.success, message: result.message },
      }));
    } catch (err) {
      setTestResult((prev) => ({
        ...prev,
        [credential.uuid]: {
          ok: false,
          message: err instanceof Error ? err.message : 'Test failed',
        },
      }));
    } finally {
      setTestingUuid(null);
    }
  }

  // Slack re-authorization: opens the same OAuth popup flow from the view pane.
  // Re-connecting updates the existing credential row (idempotent upsert on the backend).
  async function handleSlackRefreshMembers() {
    setSlackRefreshing(true);
    try {
      await apiFetch<{ ok: boolean }>('/api/integration/slack/refresh-members', {
        method: 'POST',
      });
    } catch (err) {
      setSlackBanner({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to refresh Slack members',
      });
    } finally {
      setSlackRefreshing(false);
    }
  }

  async function handleSlackReconnect() {
    setSlackReconnecting(true);
    try {
      const result = await apiFetch<{ url: string }>('/api/integration/slack/connect');
      const popup = window.open(result.url, 'slack-oauth', 'width=600,height=700,popup=yes');
      if (!popup) {
        setSlackBanner({
          type: 'error',
          message: 'Popup was blocked. Please allow popups for this site and try again.',
        });
      }
    } catch (err) {
      setSlackBanner({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to start Slack re-authorization',
      });
    } finally {
      setSlackReconnecting(false);
    }
  }

  async function handleToggleStatus(credential: IntegrationCredential) {
    setTogglingUuid(credential.uuid);
    const nextStatus = credential.lifecycleStatus === 'active' ? 'disabled' : 'active';
    try {
      await apiFetch(`/api/integration/${credential.uuid}/status`, {
        method: 'PUT',
        body: JSON.stringify({ lifecycleStatus: nextStatus }),
      });
      setIntegrations((prev) =>
        prev.map((c) => (c.uuid === credential.uuid ? { ...c, lifecycleStatus: nextStatus } : c))
      );
    } catch (err) {
      console.error('Failed to update lifecycle status:', err);
    } finally {
      setTogglingUuid(null);
    }
  }

  // Group catalog by category
  const drawerOpen = selectedCredential != null || createPane != null || catalogOpen;
  const drawerTitle =
    catalogOpen && !createPane && !selectedCredential
      ? 'Connect'
      : createPane
        ? `Connect ${createPane.name}`
        : (selectedCredential?.displayName ?? '');

  // Project the inline ContextDrawer as a flex sibling of the content column
  React.useEffect(() => {
    if (!sidePanelCtx) return;
    sidePanelCtx.setPanel(
      <ContextDrawer
        inline
        open={!isLoading && drawerOpen}
        onClose={handleCloseDrawer}
        title={isLoading ? '' : drawerTitle}
      >
        {isLoading && <IntegrationsDetailSkeleton />}

        {/* Catalog pane - shown when Connect button is clicked */}
        {!isLoading && catalogOpen && !createPane && !selectedCredential && (
          <CatalogPanel
            catalog={catalog}
            isWorkspaceAdmin={isWorkspaceAdmin}
            activeModuleKey={activeModuleKey}
            onSelect={handleSelectFromCatalog}
            onClose={handleCloseDrawer}
          />
        )}

        {/* Create pane - OAuth integrations use dedicated panels; others use the manual form */}
        {!isLoading && createPane && createPane.slug === 'slack' && (
          <SlackConnectPanel key={createPane.uuid} onCancel={handleCloseCreate} />
        )}
        {!isLoading && createPane && createPane.slug === 'google-calendar' && (
          <GoogleCalendarConnectPanel
            key={createPane.uuid}
            onCancel={handleCloseCreate}
            onSaved={() => {
              void loadData();
              window.dispatchEvent(new CustomEvent('gcal-workspace-config-changed'));
            }}
            isWorkspaceAdmin={isWorkspaceAdmin}
          />
        )}
        {!isLoading && createPane && createPane.slug === 'google-drive' && (
          <GoogleDriveConnectPanel key={createPane.uuid} onCancel={handleCloseCreate} />
        )}
        {!isLoading && createPane && createPane.slug === 'zoom' && (
          <ZoomConnectPanel
            key={createPane.uuid}
            onCancel={handleCloseCreate}
            onPopupClosed={() => {
              setCreatePane(null);
              void loadData();
            }}
          />
        )}
        {!isLoading &&
          createPane &&
          createPane.slug !== 'slack' &&
          createPane.slug !== 'google-calendar' &&
          createPane.slug !== 'google-drive' &&
          createPane.slug !== 'zoom' && (
            <CreatePanel
              key={createPane.uuid}
              definition={createPane}
              onCancel={handleCloseCreate}
              onCreated={handleCreateSuccess}
            />
          )}

        {/* Credential detail / edit / rotate / delete pane */}
        {!isLoading && !createPane && selectedCredential && (
          <>
            {rpMode === 'view' && (
              <div className="integrations-detail-view">
                {/* Provider identity */}
                <div className="integrations-detail-provider">
                  <div
                    className={`integrations-detail-provider-logo${hasLogo(selectedCredential.definition.slug) ? ' has-logo' : ''}`}
                  >
                    <ProviderLogo
                      slug={selectedCredential.definition.slug}
                      name={selectedCredential.definition.name}
                      size={20}
                    />
                  </div>
                  <span className="integrations-detail-provider-name">
                    {selectedCredential.definition.name}
                  </span>
                </div>

                {/* Status badges */}
                <div className="integrations-detail-badges">
                  <StatusBadge
                    variant={selectedCredential.lifecycleStatus}
                    label={selectedCredential.lifecycleStatus}
                  />
                  {selectedCredential.healthStatus !== 'unknown' &&
                    selectedCredential.definition.slug !== 'google-drive' && (
                      <StatusBadge
                        variant={selectedCredential.healthStatus}
                        label={selectedCredential.healthStatus}
                      />
                    )}
                </div>

                {/* Credential summary */}
                {selectedCredential.definition.slug !== 'google-drive' && (
                  <MaskedCredentialPreview fields={selectedCredential.maskedCredentials} />
                )}

                {/* Non-secret config values (e.g. account_id for Zoom) */}
                {selectedCredential.config.length > 0 && (
                  <span className="integration-item-credential-preview">
                    {selectedCredential.config.map((c) => `${c.key}: ${c.value}`).join('  ·  ')}
                  </span>
                )}

                {selectedCredential.description && (
                  <p className="integrations-detail-description">
                    {selectedCredential.description}
                  </p>
                )}

                {selectedCredential.credentialExpiresAt && (
                  <p className="integrations-detail-expiry">
                    Expires {formatDate(selectedCredential.credentialExpiresAt)}
                  </p>
                )}

                {/* Test result */}
                {(() => {
                  const result = testResult[selectedCredential.uuid];
                  if (!result) return null;
                  return (
                    <span
                      className={`integrations-status-message ${
                        result.ok
                          ? 'integrations-status-message-success'
                          : 'integrations-status-message-error'
                      }`}
                    >
                      {result.ok ? 'Connection successful' : (result.message ?? 'Test failed')}
                    </span>
                  );
                })()}

                {/* Google Calendar: direct users to the Meetings page for personal connect */}
                {selectedCredential.definition.slug === 'google-calendar' && (
                  <p className="integrations-field-hint">
                    To connect or reconnect your personal Google Calendar, open a project, go to the{' '}
                    <strong>Meetings</strong> page, and click <strong>Settings</strong> in the right
                    panel.
                  </p>
                )}

                {/* Admin actions */}
                {isWorkspaceAdmin && (
                  <div className="integrations-detail-actions">
                    <div className="integrations-detail-divider" />
                    {selectedCredential.definition.supportsTest &&
                      selectedCredential.definition.slug !== 'google-drive' && (
                        <button
                          type="button"
                          className="integrations-btn integrations-btn-secondary"
                          onClick={() => void handleTest(selectedCredential)}
                          disabled={testingUuid === selectedCredential.uuid}
                        >
                          {testingUuid === selectedCredential.uuid ? 'Testing…' : 'Test connection'}
                        </button>
                      )}
                    {selectedCredential.definition.slug !== 'zoom' &&
                      selectedCredential.definition.slug !== 'google-drive' && (
                        <button
                          type="button"
                          className="integrations-btn integrations-btn-secondary"
                          onClick={() => void handleToggleStatus(selectedCredential)}
                          disabled={togglingUuid === selectedCredential.uuid}
                        >
                          {selectedCredential.lifecycleStatus === 'active' ? 'Disable' : 'Enable'}
                        </button>
                      )}
                    {/* Slack and Google Calendar use OAuth - manual edit/rotate are inapplicable.
                        Re-authorization re-runs the OAuth flow and updates the existing credential. */}
                    {selectedCredential.definition.slug === 'slack' ? (
                      <>
                        <button
                          type="button"
                          className="integrations-btn integrations-btn-secondary"
                          onClick={() => void handleSlackReconnect()}
                          disabled={slackReconnecting}
                        >
                          {slackReconnecting ? 'Redirecting…' : 'Reconnect'}
                        </button>
                      </>
                    ) : selectedCredential.definition.slug === 'google-calendar' ? (
                      <>
                        <button
                          type="button"
                          className="integrations-btn integrations-btn-secondary"
                          onClick={() => {
                            const def = catalog.find(
                              (d) => d.slug === selectedCredential.definition.slug
                            );
                            if (def) setCreatePane(def);
                          }}
                        >
                          Edit settings
                        </button>
                      </>
                    ) : selectedCredential.definition.slug === 'zoom' ? (
                      <>
                        <button
                          type="button"
                          className="integrations-btn integrations-btn-secondary"
                          onClick={() => setRpMode('edit')}
                        >
                          Edit settings
                        </button>
                      </>
                    ) : selectedCredential.definition.slug === 'google-drive' ? (
                      <>
                        <button
                          type="button"
                          className="integrations-btn integrations-btn-secondary"
                          onClick={() => setRpMode('edit')}
                        >
                          Edit settings
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="integrations-btn integrations-btn-secondary"
                          onClick={() => setRpMode('edit')}
                        >
                          Edit settings
                        </button>
                        <button
                          type="button"
                          className="integrations-btn integrations-btn-secondary"
                          onClick={() => setRpMode('rotate')}
                        >
                          Rotate credentials
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className="integrations-btn integrations-btn-danger"
                      onClick={() => setRpMode('delete')}
                    >
                      Remove
                    </button>
                    {selectedCredential.definition.slug === 'slack' && (
                      <button
                        type="button"
                        className="integrations-btn integrations-btn-secondary"
                        onClick={() => void handleSlackRefreshMembers()}
                        disabled={slackRefreshing}
                      >
                        {slackRefreshing ? 'Refreshing…' : 'Refresh members'}
                      </button>
                    )}
                  </div>
                )}

                {/* Dev-only: send a test DM directly from the Slack credential detail pane */}
                {import.meta.env.DEV &&
                  isWorkspaceAdmin &&
                  selectedCredential.definition.slug === 'slack' && <SlackTestDmPanel />}
              </div>
            )}

            {rpMode === 'edit' && (
              <EditPanel
                key={selectedCredential.uuid}
                credential={selectedCredential}
                onCancel={() => setRpMode('view')}
                onUpdated={handleCredentialUpdated}
              />
            )}

            {rpMode === 'rotate' && (
              <RotatePanel
                key={selectedCredential.uuid}
                credential={selectedCredential}
                onCancel={() => setRpMode('view')}
                onRotated={handleCredentialUpdated}
              />
            )}

            {rpMode === 'delete' && (
              <DeletePanel
                key={selectedCredential.uuid}
                credential={selectedCredential}
                onCancel={() => setRpMode('view')}
                onDeleted={handleCredentialDeleted}
              />
            )}
          </>
        )}
      </ContextDrawer>
    );
    return () => sidePanelCtx.setPanel(null);
  }, [
    sidePanelCtx,
    isLoading,
    drawerOpen,
    drawerTitle,
    catalogOpen,
    createPane,
    selectedCredential,
    rpMode,
    catalog,
    isWorkspaceAdmin,
    testingUuid,
    testResult,
    togglingUuid,
    slackReconnecting,
    slackRefreshing,
  ]);

  if (isLoading) {
    return (
      <div className="integrations-page" aria-busy="true" aria-label="Loading integrations">
        <div className="integrations-connected-grid">
          <IntegrationsConnectedCardSkeleton />
          <IntegrationsConnectedCardSkeleton />
          <IntegrationsConnectedCardSkeleton />
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="integrations-empty">
        <p className="integrations-empty-title">Failed to load</p>
        <div style={{ paddingTop: 'var(--space-2)' }}>
          <PageErrorState
            message={fetchError}
            onRetry={() => {
              setIsLoading(true);
              void loadData();
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="integrations-page">
      {/* OAuth result banners */}
      {slackBanner && (
        <div
          className={`integrations-status-message integrations-status-message-${slackBanner.type === 'success' ? 'success' : 'error'} integrations-oauth-banner`}
          role="status"
        >
          {slackBanner.message}
          <button
            type="button"
            className="integrations-oauth-banner-close"
            aria-label="Dismiss"
            onClick={() => setSlackBanner(null)}
          >
            ×
          </button>
        </div>
      )}
      {gcalBanner && (
        <div
          className={`integrations-status-message integrations-status-message-${gcalBanner.type === 'success' ? 'success' : 'error'} integrations-oauth-banner`}
          role="status"
        >
          {gcalBanner.message}
          <button
            type="button"
            className="integrations-oauth-banner-close"
            aria-label="Dismiss"
            onClick={() => setGcalBanner(null)}
          >
            ×
          </button>
        </div>
      )}
      {/* ── Connected integrations grid ─────────────────────────── */}
      {integrations.length === 0 && (
        <div className="integrations-empty">
          <p className="integrations-empty-title">No integrations connected</p>
          <p className="integrations-empty-text">
            Click Connect to add an integration to this workspace.
          </p>
        </div>
      )}
      {integrations.length > 0 && (
        <div className="integrations-connected-grid">
          {integrations.map((cred) => (
            <VendorCard
              key={cred.uuid}
              logo={
                <ProviderLogo slug={cred.definition.slug} name={cred.definition.name} size={28} />
              }
              name={cred.displayName}
              badges={
                <>
                  <StatusBadge variant={cred.lifecycleStatus} label={cred.lifecycleStatus} />
                  {cred.healthStatus !== 'unknown' && cred.definition.slug !== 'google-drive' && (
                    <StatusBadge variant={cred.healthStatus} label={cred.healthStatus} />
                  )}
                </>
              }
              isSelected={selectedCredentialUuid === cred.uuid}
              logoTransparent={hasLogo(cred.definition.slug)}
              onClick={() => handleSelectCredential(cred.uuid)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
